import { Hash, Users } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { cn } from '@/lib/utils';
import type { SlackChannel } from '@/lib/queries/integrations/messaging/slack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// Simple Badge component
const Badge = ({
  children,
  variant = 'default',
  className,
}: {
  children: React.ReactNode;
  variant?: 'default' | 'outline' | 'secondary';
  className?: string;
}) => (
  <span
    className={cn(
      'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium',
      variant === 'default' &&
        'bg-blue-100 text-blue-800 border border-blue-200',
      variant === 'outline' &&
        'bg-transparent text-gray-600 border border-gray-300',
      variant === 'secondary' &&
        'bg-gray-100 text-gray-800 border border-gray-200',
      className,
    )}
  >
    {children}
  </span>
);

interface ChannelsTableProps {
  channels: SlackChannel[];
}

export function ChannelsTable({ channels }: ChannelsTableProps): JSX.Element {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <FormattedMessage id="integrations.messaging.slack.table.channel" />
          </TableHead>
          <TableHead>
            <FormattedMessage id="integrations.messaging.slack.table.workspace" />
          </TableHead>
          <TableHead>
            <FormattedMessage id="integrations.messaging.slack.table.members" />
          </TableHead>
          <TableHead>
            <FormattedMessage id="integrations.messaging.slack.table.description" />
          </TableHead>
          <TableHead>
            <FormattedMessage id="integrations.messaging.slack.table.anniePresence" />
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {channels.map(channel => (
          <TableRow key={channel.id}>
            <TableCell className="font-medium">
              <div className="flex items-center">
                <Hash className="mr-2 h-4 w-4 text-gray-500" />
                {channel.name}
              </div>
            </TableCell>
            <TableCell>{channel.workspace}</TableCell>
            <TableCell>
              <div className="flex items-center">
                <Users className="mr-2 h-4 w-4 text-gray-500" />
                {channel.memberCount}
              </div>
            </TableCell>
            <TableCell>
              <div
                className="max-w-xs truncate"
                title={channel.description ?? undefined}
              >
                {channel.description ?? (
                  <span className="text-gray-400">
                    <FormattedMessage id="integrations.messaging.slack.table.noDescription" />
                  </span>
                )}
              </div>
            </TableCell>
            <TableCell>
              <Badge variant={channel.anniePresent ? 'default' : 'outline'}>
                {channel.anniePresent ? (
                  <FormattedMessage id="integrations.messaging.slack.annie.present" />
                ) : (
                  <FormattedMessage id="integrations.messaging.slack.annie.absent" />
                )}
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
